import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, Search, X, Eraser, CalendarDays, RotateCcw } from "lucide-react";
import "./Dashboard.css";

const MAIN_DASHBOARD_MOCK_PROPERTIES = [
  { property: "ANANDVAN", location: "Rishikesh" },
  { property: "AROGYADHAM RETREAT", location: "Rishikesh" },
  { property: "ATITHYA", location: "Rishikesh" },
  { property: "BLUE JAY HOSTEL", location: "Mussoorie" },
  { property: "CEDAR CASTLE", location: "Mussoorie" },
  { property: "COMFORT STAY - NEAR AIRPORT", location: "Lucknow" },
  { property: "EUPHORIC RIVER RESORT", location: "Rishikesh" },
  { property: "EUPHORIC TIGER RESORT", location: "Rishikesh" },
  { property: "GREEN VIEW", location: "Mussoorie" },
  { property: "HOTEL AMADEUS", location: "Ayodhya" },
  { property: "HOTEL ARDMORE HILLS", location: "Mussoorie" },
  { property: "HOTEL BALDEV PALACE", location: "Ayodhya" },
  { property: "HOTEL LUXEEVISTA", location: "Dalhousie" },
  { property: "HOTEL MANAS INN", location: "Ayodhya" },
  { property: "HOTEL MANGLAM INN NEAR AIRPORT", location: "Lucknow" },
  { property: "HOTEL NIRVANA BLISS", location: "Rishikesh" },
  { property: "HOTEL NIRVANA PALACE", location: "Vrindavan" },
  { property: "HOTEL PARK WOOD", location: "Lansdowne" },
  { property: "HOTEL SAMRAT", location: "Ayodhya" },
  { property: "HOTEL WINTER LINE INN", location: "Mussoorie" },
  { property: "HYFA CONTINENTAL", location: "Varanasi" },
  { property: "LANDOUR WOODS HOUSE", location: "Mussoorie" },
  { property: "MANGLAM INN", location: "Ayodhya" },
  { property: "MONTARA", location: "Rishikesh" },
  { property: "MURLI MANOHAR", location: "Vrindavan" },
  { property: "MUSSOORIE RESIDENCY", location: "Mussoorie" },
  { property: "NEERAJ GANGA COTTAGES", location: "Rishikesh" },
  { property: "NEERAJ GANGA DIVINE", location: "Rishikesh" },
  { property: "NEERAJ GANGA MARINE", location: "Rishikesh" },
  { property: "NEERAJ GANGA RAJ MAHAL", location: "Rishikesh" },
  { property: "NEERAJ GANGA RANI MAHAL", location: "Rishikesh" },
  { property: "OAK LEAF", location: "Dhanaulti" },
  { property: "PALM ARYAN - MALL ROAD", location: "Mussoorie" },
  { property: "RAGHUVEER SADAN", location: "Ayodhya" },
  { property: "RANI MAHAL", location: "Rishikesh" },
  { property: "RIVERLINE", location: "Rishikesh" },
  { property: "ROCKLAND RESIDENCY", location: "Mussoorie" },
  { property: "RS RESORT", location: "Ayodhya" },
  { property: "SG ROYAL PORTICO", location: "Rishikesh" },
  { property: "SHIV SHAKTI", location: "Rishikesh" },
  { property: "SHIV VILAS", location: "Rishikesh" },
  { property: "SHIVA LAKE", location: "Mussoorie" },
  { property: "SHREE SITA SADAN", location: "Ayodhya" },
  { property: "SHRI GOVINDAM BANARAS", location: "Varanasi" },
  { property: "SUN VILLAGE", location: "Mussoorie" },
  { property: "THE MAHESHWAR HOTEL", location: "Varanasi" },
  { property: "THE MAPLE WOODS RESORT", location: "Jim Corbett" },
  { property: "VASU PALACE", location: "Ayodhya" },
  { property: "VRINDA TAPOVAN HOMESTAY", location: "Rishikesh" },
  { property: "WILDLINE GOLD RESORT", location: "Jim Corbett" },
  { property: "WINTERLINE", location: "Lansdowne" },
  { property: "WINTERLINE MUSSOORIE", location: "Mussoorie" },
  { property: "WRAVELER MUSSOORIE", location: "Mussoorie" },
  { property: "WRAVELER RKSH", location: "Rishikesh" },
];

const TAG_COLOR_PALETTE = [
  "#fde2e2", "#e0f2fe", "#fef9c3", "#dcfce7", "#ede9fe", "#ffe4e6",
  "#e0e7ff", "#fce7f3", "#d1fae5", "#fef3c7", "#e2e8f0", "#cffafe",
];
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
}
function colorForValue(value) {
  if (!value) return undefined;
  const idx = hashString(value) % TAG_COLOR_PALETTE.length;
  return TAG_COLOR_PALETTE[idx];
}
const RATING_OUT_OF_10_PLATFORMS = new Set(["agoda", "booking", "booking.com", "expedia", "hotels"]);
const RATING_OUT_OF_5_PLATFORMS = new Set(["makemytrip", "mmt", "goibibo", "google", "tripadvisor"]);
function getRatingColor(platform, rating) {
  if (rating === null || rating === undefined || rating === "") return "#FFFFFF";
  const plat = String(platform || "").trim().toLowerCase();
  // RATING COLOUR CODE:
  //   out of 10: white 0 | red 0.1–6.0 | yellow 6.1–8.0 | light green 8.1–9.0 | dark green 9.1–10
  //   out of 5:  white 0 | red 0.1–3.0 | yellow 3.1–4.0 | light green 4.1–4.5 | dark green 4.6–5.0
  // Judged on the value as SHOWN (1 decimal), so "8.0" is always yellow
  // and "8.1" always light green.
  const value = Math.round(Number(rating) * 10) / 10;
  if (!(value > 0)) return "#FFFFFF";
  if (RATING_OUT_OF_10_PLATFORMS.has(plat)) {
    if (value >= 9.1) return "#66BB6A";
    if (value >= 8.1) return "#A5D6A7";
    if (value >= 6.1) return "#FFF59D";
    return "#EF9A9A";
  }
  if (RATING_OUT_OF_5_PLATFORMS.has(plat)) {
    if (value >= 4.6) return "#66BB6A";
    if (value >= 4.1) return "#A5D6A7";
    if (value >= 3.1) return "#FFF59D";
    return "#EF9A9A";
  }
  return "#FFFFFF";
}
function getRevenueColor(pct) {
  if (pct === null || pct === undefined) return undefined;
  if (pct < 70) return "#EF9A9A";
  if (pct < 80) return "#FFCC80";
  if (pct < 90) return "#FFF59D";
  if (pct < 100) return "#A5D6A7";
  return "#66BB6A";
}
function getSoldInventoryColor(sold, total) {
  return sold === total ? "#A5D6A7" : "#FFCC80";
}

function formatDateLabel(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
// AGENT-LINK FILTERING SUPPORT — same as Dashboard.jsx's helper. Appends
// the logged-in user's username to every dashboard API call, so the
// backend's agentFilter.js can restrict results to just that user's
// linked agent (OTA EXECUTIVE / OTA MANAGER only). Unlinked users and
// every other role are unaffected.
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}

const PERCENT_LABELS = new Set(["% REV", "OCCUPANCY %", "CONV%"]);
const CURRENCY_LABELS = new Set([
  "TARGET", "REVENUE", "REVENUE (REVX)", "REVENUE (MKT)",
  "ADR", "ADR (REVX)", "ADR (MKT)", "ASP",
]);
function formatCellValue(group, label, rawValue) {
  if (rawValue === null || rawValue === undefined) return rawValue;
  const num = Number(rawValue);
  if (isNaN(num)) return rawValue;
  if (PERCENT_LABELS.has(label)) return `${num.toFixed(1)}%`;
  if (group === "RATE PARITY" || CURRENCY_LABELS.has(label)) {
    return `₹${Math.round(num).toLocaleString("en-IN")}`;
  }
  return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

const MMT_METRIC_ALIASES = {
  "VISITS": "Property Visits",
  "CONV%": "Conversion",
  "RN": "Room Nights",
};

const COLUMN_FILTER_GROUPS = [
  { group: "RANKLY", columns: ["AGODA", "BOOKING", "MAKEMYTRIP", "GOIBIBO", "GOOGLE", "EXPEDIA", "HOTELS", "TRIPADVISOR"] },
  { group: "REVENUE DASHBOARD", columns: ["TARGET", "REVENUE", "% REV"] },
  { group: "BOOKING DASHBOARD", columns: ["RANK", "RN (REVX)", "RN (MKT)", "ADR (REVX)", "ADR (MKT)", "REVENUE (REVX)", "REVENUE (MKT)", "BOOKINGS", "CONV%", "VISITS"] },
  { group: "MMT DASHBOARD", columns: ["RATING (MMT)", "RATING (GOIBIBO)", "RN", "REVENUE", "ASP", "VISITS", "CONV%"] },
  { group: "OCCUPANCY DASHBOARD", columns: ["TOTAL ROOMS", "AVAILABLE", "SOLD", "OCCUPANCY %", "ADR"] },
  { group: "BOOK PER DAY", columns: ["BOOKINGS", "RN", "ADR", "REVENUE"] },
  {
    group: "RATE PARITY",
    columns: [
      "Agoda", "Booking.com", "MakeMyTrip.com", "Expedia.co.in", "Hotels.com",
      "Cleartrip.com", "EaseMyTrip.com", "Yatra.com", "Tripadvisor.in",
      "Hostelworld", "Trip.com", "Traveloka.com", "BookMyBooking.com",
      "Bluepillow.in", "Qantas Hotels", "Skyscanner", "Vio.com", "Wego",
      "dealbase.com", "ixigo", "muv AI",
    ],
  },
];

function columnKey(group, column) {
  return `${group}::${column}`;
}

function ColumnGroupRow({ group, columns, selected, onToggleColumn, onToggleGroupAll, isExpanded, onToggleExpanded }) {
  const checkboxRef = useRef(null);
  const groupKeys = columns.map((c) => columnKey(group, c));
  const allSelected = groupKeys.length > 0 && groupKeys.every((k) => selected.includes(k));
  const someSelected = groupKeys.some((k) => selected.includes(k));

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  return (
    <div>
      <div
        className="rd-option"
        style={{ fontWeight: 700, justifyContent: "space-between", display: "flex", alignItems: "center" }}
      >
        <label
          style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer" }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            ref={checkboxRef}
            checked={allSelected}
            onChange={() => onToggleGroupAll(groupKeys, allSelected)}
            title={`Select all ${group} columns`}
          />
          <span onClick={() => onToggleExpanded(group)} style={{ cursor: "pointer", flex: 1 }}>
            {group}
          </span>
        </label>
        <ChevronDown
          size={14}
          onClick={() => onToggleExpanded(group)}
          style={{ cursor: "pointer", transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </div>
      {isExpanded &&
        columns.map((col) => {
          const key = columnKey(group, col);
          const isSelected = selected.includes(key);
          return (
            <label key={key} className={`rd-option${isSelected ? " rd-selected" : ""}`} style={{ paddingLeft: 28 }}>
              <input type="checkbox" checked={isSelected} onChange={() => onToggleColumn(key)} />
              <span>{col}</span>
            </label>
          );
        })}
    </div>
  );
}

function ColumnsFilter({ selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleGroupExpanded(group) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  function toggleColumn(key) {
    if (selected.includes(key)) onChange(selected.filter((k) => k !== key));
    else onChange([...selected, key]);
  }

  function toggleGroupAll(groupKeys, currentlyAllSelected) {
    if (currentlyAllSelected) {
      onChange(selected.filter((k) => !groupKeys.includes(k)));
    } else {
      const merged = new Set([...selected, ...groupKeys]);
      onChange(Array.from(merged));
    }
  }

  const summary = selected.length === 0 ? "All" : `${selected.length} selected`;

  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <FilterLabel label="Columns" onClear={onClear} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel" style={{ minWidth: 320 }}>
          {COLUMN_FILTER_GROUPS.map(({ group, columns }) => (
            <ColumnGroupRow
              key={group}
              group={group}
              columns={columns}
              selected={selected}
              onToggleColumn={toggleColumn}
              onToggleGroupAll={toggleGroupAll}
              isExpanded={expandedGroups.has(group)}
              onToggleExpanded={toggleGroupExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
        <Eraser size={12} />
      </button>
    </div>
  );
}

function MultiSelectFilter({ label, options, selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleOption(opt) {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selected.includes(o));
  const someFilteredSelected = filteredOptions.some((o) => selected.includes(o));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  function toggleSelectAll() {
    if (allFilteredSelected) {
      onChange(selected.filter((s) => !filteredOptions.includes(s)));
    } else {
      const merged = new Set([...selected, ...filteredOptions]);
      onChange(Array.from(merged));
    }
  }

  const summary =
    selected.length === 0
      ? "All"
      : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <FilterLabel label={label} onClear={onClear} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="rd-dropdown-search-clear"
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {filteredOptions.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input
                type="checkbox"
                ref={selectAllRef}
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
              />
              <span>Select All{query.trim() ? ` (${filteredOptions.length} shown)` : ""}</span>
            </label>
          )}

          {filteredOptions.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <label key={opt} className={`rd-option${isSelected ? " rd-selected" : ""}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleOption(opt)} />
                  <span>{opt}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, onClear }) {
  return (
    <div className="rd-filter-group rd-search-group">
      <FilterLabel label="Search" onClear={onClear} />
      <div className="rd-search-input-wrap">
        <Search size={14} className="rd-search-icon" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Property, location..."
          className="rd-search-input"
        />
        {value && (
          <button onClick={onClear} className="rd-search-clear-btn" title="Clear search">
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function MonthFilter({ availableMonths, selectedMonth, onChange, onClear }) {
  return (
    <div className="rd-filter-group">
      <FilterLabel label="Month" onClear={onClear} />
      <select value={selectedMonth} onChange={(e) => onChange(e.target.value)} className="rd-dropdown-btn">
        <option value="">All</option>
        {availableMonths.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}
function isoFromParts(y, m, d) {
  return `${y}-${pad2(m + 1)}-${pad2(d)}`;
}
function isoFromDate(dt) {
  return isoFromParts(dt.getFullYear(), dt.getMonth(), dt.getDate());
}
function partsFromISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m: m - 1, d };
}
function dateFromISO(iso) {
  const { y, m, d } = partsFromISO(iso);
  return new Date(y, m, d);
}
function formatDisplayDate(iso) {
  if (!iso) return "—";
  return dateFromISO(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const QUICK_RANGE_OPTIONS = [
  "Today", "Yesterday", "This Week", "Last Week", "This Month",
  "Last Month", "Next month", "Last Year", "This Year",
];

function computeQuickRange(presetLabel) {
  const now = new Date();
  const ty = now.getFullYear();
  const tm = now.getMonth();
  const td = now.getDate();
  const today = new Date(ty, tm, td);
  switch (presetLabel) {
    case "Today":
      return [isoFromDate(today), isoFromDate(today)];
    case "Yesterday": {
      const y = new Date(ty, tm, td - 1);
      return [isoFromDate(y), isoFromDate(y)];
    }
    case "This Week": {
      const start = new Date(ty, tm, td - today.getDay());
      const end = new Date(ty, tm, td - today.getDay() + 6);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "Last Week": {
      const start = new Date(ty, tm, td - today.getDay() - 7);
      const end = new Date(ty, tm, td - today.getDay() - 1);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "This Month": {
      const start = new Date(ty, tm, 1);
      const end = new Date(ty, tm + 1, 0);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "Last Month": {
      const start = new Date(ty, tm - 1, 1);
      const end = new Date(ty, tm, 0);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "Next month": {
      const start = new Date(ty, tm + 1, 1);
      const end = new Date(ty, tm + 2, 0);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "Last Year": {
      const start = new Date(ty - 1, 0, 1);
      const end = new Date(ty - 1, 11, 31);
      return [isoFromDate(start), isoFromDate(end)];
    }
    case "This Year": {
      const start = new Date(ty, 0, 1);
      const end = new Date(ty, 11, 31);
      return [isoFromDate(start), isoFromDate(end)];
    }
    default:
      return null;
  }
}

function buildCalendarGrid(viewYear, viewMonth) {
  const startWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push({ day: daysInPrevMonth - startWeekday + 1 + i, inMonth: false, iso: null });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, inMonth: true, iso: isoFromParts(viewYear, viewMonth, day) });
  }
  let trailingDay = 1;
  while (cells.length < 42) {
    cells.push({ day: trailingDay++, inMonth: false, iso: null });
  }
  return cells;
}
function yearOptionsAround(centerYear) {
  const years = [];
  for (let y = centerYear - 15; y <= centerYear + 15; y++) years.push(y);
  return years;
}

function DateRangeCalendarPopover({ fromDate, toDate, onApply, onClose }) {
  const anchor = dateFromISO(toDate || fromDate);
  const [viewYear, setViewYear] = useState(anchor.getFullYear());
  const [viewMonth, setViewMonth] = useState(anchor.getMonth());
  const [pendingStart, setPendingStart] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  function goToPrevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  }
  function goToNextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  }

  function applyRange(isoA, isoB) {
    const finalFrom = isoA <= isoB ? isoA : isoB;
    const finalTo = isoA <= isoB ? isoB : isoA;
    onApply(finalFrom, finalTo);
  }

  function handlePresetClick(label) {
    const range = computeQuickRange(label);
    if (!range) return;
    setPendingStart(null);
    applyRange(range[0], range[1]);
    onClose();
  }

  function handleDayClick(iso) {
    if (!iso) return;
    if (pendingStart === null) {
      setPendingStart(iso);
      onApply(iso, iso);
    } else {
      applyRange(pendingStart, iso);
      setPendingStart(null);
      onClose();
    }
  }

  const cells = buildCalendarGrid(viewYear, viewMonth);
  const activeField = pendingStart ? "to" : "from";
  const now = new Date();
  const todayISO = isoFromDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));

  return (
    <div className="rd-daterange-popover" ref={popoverRef}>
      <div className="rd-daterange-presets">
        {QUICK_RANGE_OPTIONS.map((label) => (
          <button key={label} type="button" className="rd-daterange-preset-btn" onClick={() => handlePresetClick(label)}>
            {label}
          </button>
        ))}
      </div>
      <div className="rd-daterange-calendar">
        <div className="rd-daterange-readouts">
          <div className={`rd-daterange-readout${activeField === "from" ? " rd-daterange-readout-active" : ""}`}>
            {formatDisplayDate(fromDate)}
          </div>
          <div className={`rd-daterange-readout${activeField === "to" ? " rd-daterange-readout-active" : ""}`}>
            {formatDisplayDate(toDate)}
          </div>
        </div>
        <div className="rd-daterange-nav">
          <button type="button" className="rd-daterange-nav-btn" onClick={goToPrevMonth} title="Previous month">
            <ChevronDown size={14} style={{ transform: "rotate(90deg)" }} />
          </button>
          <div className="rd-daterange-monthyear">
            <select value={viewMonth} onChange={(e) => setViewMonth(Number(e.target.value))}>
              {MONTH_LABELS.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>
            <select value={viewYear} onChange={(e) => setViewYear(Number(e.target.value))}>
              {yearOptionsAround(viewYear).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button type="button" className="rd-daterange-nav-btn" onClick={goToNextMonth} title="Next month">
            <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
          </button>
        </div>
        <div className="rd-daterange-weekdays">
          {WEEKDAY_LABELS.map((w) => <span key={w}>{w}</span>)}
        </div>
        <div className="rd-daterange-grid">
          {cells.map((cell, idx) => {
            const isSelectedEdge = cell.iso && (cell.iso === fromDate || cell.iso === toDate);
            const isInRange = cell.iso && fromDate && toDate && cell.iso > fromDate && cell.iso < toDate;
            const isToday = cell.iso === todayISO;
            const classes = [
              "rd-daterange-day",
              !cell.inMonth ? "rd-daterange-day-outside" : "",
              isInRange ? "rd-daterange-day-inrange" : "",
              isSelectedEdge ? "rd-daterange-day-selected" : "",
              isToday ? "rd-daterange-day-today" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={idx}
                type="button"
                className={classes}
                disabled={!cell.inMonth}
                onClick={() => handleDayClick(cell.iso)}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DateRangeControl({ availableDates, fromDate, toDate, onRangeChange, onClear, onToday, label = "DATE RANGE" }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="occ-date-group" style={{ position: "relative" }}>
      <div className="rd-filter-label-row">
        <label className="rd-filter-label">
          <CalendarDays size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          {label}
        </label>
        <button onClick={onClear} title="Clear date range" className="rd-clear-btn">
          <Eraser size={12} />
        </button>
      </div>
      <div className="occ-date-inputs">
        <button type="button" className="rd-dropdown-btn rd-daterange-trigger" onClick={() => setOpen((o) => !o)}>
          <span className="rd-dropdown-summary">
            {formatDisplayDate(fromDate)} <span className="rd-date-sep">to</span> {formatDisplayDate(toDate)}
          </span>
          <ChevronDown size={14} />
        </button>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today">
          <RotateCcw size={12} />
          TODAY
        </button>
      </div>
      {open && (
        <DateRangeCalendarPopover
          fromDate={fromDate}
          toDate={toDate}
          onApply={onRangeChange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export default function VarianceDashboard() {
  // Mobile: filters collapse behind a "Filters" toggle, same as Rankly and
  // the other dashboards (desktop always shows them — see Dashboard.css).
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [revPctFilterMax, setRevPctFilterMax] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [metricDisplayModes, setMetricDisplayModes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortKey, setSortKey] = useState("property");
  const [expandedProperties, setExpandedProperties] = useState(new Set());

  const [propertiesMaster, setPropertiesMaster] = useState([]);
  const [ranklyRows, setRanklyRows] = useState([]);
  const [revenueRows, setRevenueRows] = useState([]);
  const [occupancyRows, setOccupancyRows] = useState([]);
  const [bookPerDayRows, setBookPerDayRows] = useState([]);
  const [mmtRows, setMmtRows] = useState([]);
  const [rateParityRows, setRateParityRows] = useState([]);
  const [bookingOverviewRows, setBookingOverviewRows] = useState([]);
  const [bookingVisitConversionRows, setBookingVisitConversionRows] = useState([]);
  const [bookingRankDataRows, setBookingRankDataRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  useEffect(() => {
    const endpoints = [
      { key: "properties", url: "/api/properties/master", setter: setPropertiesMaster },
      { key: "rankly", url: "/api/rankly/summary", setter: setRanklyRows },
      { key: "revenue", url: "/api/revenue/summary", setter: setRevenueRows },
      { key: "occupancy", url: "/api/occupancy/summary", setter: setOccupancyRows },
      { key: "bookperday", url: "/api/bookperday/summary", setter: setBookPerDayRows },
      { key: "mmt", url: "/api/mmt/summary", setter: setMmtRows },
      { key: "rateparity", url: "/api/rateparity/summary", setter: setRateParityRows },
      { key: "bookingOverview", url: "/api/booking/overview", setter: setBookingOverviewRows },
      { key: "bookingVisitConversion", url: "/api/booking/visit-conversion", setter: setBookingVisitConversionRows },
      { key: "bookingRankData", url: "/api/booking/rank-data", setter: setBookingRankDataRows },
    ];
    Promise.allSettled(
      endpoints.map(({ url }) => fetch(apiUrl(url)).then((res) => res.json()))
    ).then((results) => {
      const errors = [];
      results.forEach((result, i) => {
        const { key, setter } = endpoints[i];
        if (result.status === "fulfilled" && result.value.success) {
          setter(result.value.data || []);
        } else {
          errors.push(key);
          setter([]);
        }
      });
      setLoadErrors(errors);
      setLoading(false);
    });
  }, []);

  function norm(s) {
    return String(s || "").trim().toUpperCase();
  }

  const availableDates = useMemo(() => {
    const dates = [];
    for (let i = -30; i <= 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  }, []);
  const realToday = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(realToday);
  const [toDate, setToDate] = useState(realToday);

  function goToToday() {
    setFromDate(realToday);
    setToDate(realToday);
  }
  function clearDates() {
    goToToday();
  }
  function applyPeriodARange(newFrom, newTo) {
    setFromDate(newFrom);
    setToDate(newTo);
  }

  const [fromDateB, setFromDateB] = useState(realToday);
  const [toDateB, setToDateB] = useState(realToday);
  function goToTodayB() {
    setFromDateB(realToday);
    setToDateB(realToday);
  }
  function clearDatesB() {
    goToTodayB();
  }
  function applyPeriodBRange(newFrom, newTo) {
    setFromDateB(newFrom);
    setToDateB(newTo);
  }

  const availableMonths = useMemo(() => {
    const seen = new Map();
    for (const d of availableDates) {
      const ym = d.slice(0, 7);
      if (!seen.has(ym)) {
        const label = new Date(d).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        seen.set(ym, { value: ym, label });
      }
    }
    return Array.from(seen.values());
  }, [availableDates]);
  const [selectedMonth, setSelectedMonth] = useState("");

  function handleMonthChange(ym) {
    setSelectedMonth(ym);
    if (!ym) return;
    const [y, m] = ym.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    setFromDate(isoFromDate(first));
    setToDate(isoFromDate(last));
  }
  function clearMonth() {
    setSelectedMonth("");
  }
  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSelectedColumns([]);
    setSearchQuery("");
    setSelectedMonth("");
    setRevPctFilterMax(null);
    clearDates();
    clearDatesB();
  }
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }

  const realProperties = useMemo(() => {
    return propertiesMaster
      .filter((p) => p.master_id)
      .map((p) => ({
        master_id: String(p.master_id),
        property: p.property_name || p.master_id,
        location: p.location || "",
        agent: p.agent || "",
      }));
  }, [propertiesMaster]);

  const propertyList = loading && realProperties.length === 0 ? MAIN_DASHBOARD_MOCK_PROPERTIES : realProperties;

  const masterIdToStayflexiHotelId = useMemo(() => {
    const map = new Map();
    for (const p of propertiesMaster) {
      if (p.master_id && p.sf_pms_id) map.set(String(p.master_id), String(p.sf_pms_id));
    }
    return map;
  }, [propertiesMaster]);

  const masterIdToRateParityId = useMemo(() => {
    const map = new Map();
    for (const p of propertiesMaster) {
      if (p.master_id && p.rate_parity_id) map.set(String(p.master_id), String(p.rate_parity_id));
    }
    return map;
  }, [propertiesMaster]);

  const masterIdToBookingId = useMemo(() => {
    const map = new Map();
    for (const p of propertiesMaster) {
      if (p.master_id && p.booking_id) map.set(String(p.master_id), String(p.booking_id));
    }
    return map;
  }, [propertiesMaster]);

  const ranklyIndex = useMemo(() => {
    const m = new Map();
    for (const r of ranklyRows) {
      if (!r.master_id) continue;
      const key = `${r.master_id}|${r.record_date}|${norm(r.platform)}`;
      if (!m.has(key)) m.set(key, r.avg_rating);
    }
    return m;
  }, [ranklyRows]);

  const revenueIndex = useMemo(() => {
    const m = new Map();
    for (const r of revenueRows) {
      if (!r.master_id) continue;
      const key = `${r.master_id}|${r.record_date}`;
      let entry = m.get(key);
      if (!entry) {
        entry = { target: r.target, totalRevenue: 0 };
        m.set(key, entry);
      }
      entry.totalRevenue += Number(r.revenue) || 0;
    }
    return m;
  }, [revenueRows]);

  const occupancyIndex = useMemo(() => {
    const overallByKey = new Map();
    const rowsByMasterDate = new Map();
    const byMasterDateCategory = new Map();
    const categoriesByMaster = new Map();
    for (const r of occupancyRows) {
      if (!r.master_id) continue;
      const masterId = String(r.master_id);
      const date = r.report_date;
      const keyMD = `${masterId}|${date}`;
      if (!overallByKey.has(keyMD)) overallByKey.set(keyMD, r);
      if (!rowsByMasterDate.has(keyMD)) rowsByMasterDate.set(keyMD, []);
      rowsByMasterDate.get(keyMD).push(r);
      if (r.room_category_name) {
        const keyMDC = `${keyMD}|${r.room_category_name}`;
        if (!byMasterDateCategory.has(keyMDC)) byMasterDateCategory.set(keyMDC, r);
        if (!categoriesByMaster.has(masterId)) categoriesByMaster.set(masterId, new Set());
        categoriesByMaster.get(masterId).add(r.room_category_name);
      }
    }
    return { overallByKey, rowsByMasterDate, byMasterDateCategory, categoriesByMaster };
  }, [occupancyRows]);

  const bookPerDayIndex = useMemo(() => {
    const m = new Map();
    for (const r of bookPerDayRows) {
      if (!r.hotel_id) continue;
      const key = `${r.hotel_id}|${r.booking_date}`;
      let entry = m.get(key);
      if (!entry) {
        entry = { bookings: 0, roomNights: 0, revenue: 0 };
        m.set(key, entry);
      }
      entry.bookings += Number(r.no_of_bookings) || 0;
      entry.roomNights += Number(r.picked_room_nights) || 0;
      entry.revenue += Number(r.room_revenue_before_tax) || 0;
    }
    return m;
  }, [bookPerDayRows]);

  const mmtIndex = useMemo(() => {
    const m = new Map();
    for (const r of mmtRows) {
      if (!r.master_id) continue;
      const key = `${r.master_id}|${r.date}|${norm(r.metric)}`;
      if (!m.has(key)) m.set(key, r.value);
    }
    return m;
  }, [mmtRows]);

  const rateParityIndex = useMemo(() => {
    const m = new Map();
    for (const r of rateParityRows) {
      if (!r.property_id) continue;
      const key = `${r.property_id}|${r.check_in}|${norm(r.ota_name)}`;
      if (!m.has(key)) m.set(key, r.price);
    }
    return m;
  }, [rateParityRows]);

  const bookingOverviewIndex = useMemo(() => {
    const m = new Map();
    for (const r of bookingOverviewRows) {
      if (!r.hotel_code) continue;
      const key = `${r.hotel_code}|${r.record_date}`;
      if (!m.has(key)) m.set(key, r);
    }
    return m;
  }, [bookingOverviewRows]);

  const bookingVisitConversionIndex = useMemo(() => {
    const m = new Map();
    for (const r of bookingVisitConversionRows) {
      if (!r.hotel_code) continue;
      const key = `${r.hotel_code}|${r.record_date}`;
      if (!m.has(key)) m.set(key, r);
    }
    return m;
  }, [bookingVisitConversionRows]);

  const bookingRankIndex = useMemo(() => {
    const m = new Map();
    for (const r of bookingRankDataRows) {
      if (!r.hotel_code) continue;
      const key = String(r.hotel_code);
      if (!m.has(key)) m.set(key, r);
    }
    return m;
  }, [bookingRankDataRows]);

  const propertyOptions = useMemo(
    () => Array.from(new Set(propertyList.map((p) => p.property))).sort(),
    [propertyList]
  );
  const locationOptions = useMemo(
    () => Array.from(new Set(propertyList.map((p) => p.location).filter(Boolean))).sort(),
    [propertyList]
  );
  const agentOptions = useMemo(
    () => Array.from(new Set(propertyList.map((p) => p.agent).filter(Boolean))).sort(),
    [propertyList]
  );

  function enumerateDatesInclusive(fromISO, toISO) {
    if (!fromISO || !toISO || fromISO > toISO) return [];
    const dates = [];
    let cursor = dateFromISO(fromISO);
    const end = dateFromISO(toISO);
    let guard = 0;
    while (cursor <= end && guard < 2000) {
      dates.push(isoFromDate(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
      guard++;
    }
    return dates;
  }

  const dateColumns = useMemo(() => enumerateDatesInclusive(fromDate, toDate), [fromDate, toDate]);

  const dateColumnsB = useMemo(() => enumerateDatesInclusive(fromDateB, toDateB), [fromDateB, toDateB]);

  const filteredRows = useMemo(() => {
    let list = propertyList;
    if (selectedProperties.length > 0) {
      list = list.filter((r) => selectedProperties.includes(r.property));
    }
    if (selectedLocations.length > 0) {
      list = list.filter((r) => selectedLocations.includes(r.location));
    }
    if (selectedAgents.length > 0) {
      list = list.filter((r) => selectedAgents.includes(r.agent));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((r) => r.property.toLowerCase().includes(q));
    }
    if (revPctFilterMax !== null) {
      list = list.filter((r) => {
        const targetSum = revenueTargetSumAcrossDates(r.master_id);
        if (targetSum <= 0) return false;
        const revSum = revenueRevenueSumAcrossDates(r.master_id);
        const pct = (revSum / targetSum) * 100;
        return pct <= revPctFilterMax;
      });
    }
    return list;
  }, [propertyList, selectedProperties, selectedLocations, selectedAgents, searchQuery, revPctFilterMax, dateColumns, revenueRows]);

  const orderedSelectedColumns = useMemo(() => {
    const defaultOrdered = [];
    for (const { group, columns } of COLUMN_FILTER_GROUPS) {
      for (const col of columns) {
        const key = columnKey(group, col);
        if (selectedColumns.includes(key)) defaultOrdered.push({ key, label: col, group });
      }
    }
    if (columnOrder.length === 0) return defaultOrdered;
    const byKey = new Map(defaultOrdered.map((c) => [c.key, c]));
    const ordered = [];
    for (const k of columnOrder) {
      if (byKey.has(k)) {
        ordered.push(byKey.get(k));
        byKey.delete(k);
      }
    }
    for (const c of defaultOrdered) {
      if (byKey.has(c.key)) ordered.push(c);
    }
    return ordered;
  }, [selectedColumns, columnOrder]);

  const rowTotalsByMasterId = useMemo(() => {
    const map = new Map();
    for (const row of filteredRows) {
      const colSums = new Map();
      for (const { key, label, group } of orderedSelectedColumns) {
        if (isLatestOnlyColumn(group, label) || isRevenuePctTotalColumn(group, label)) {
          continue;
        }
        let sum = 0;
        for (const d of dateColumns) {
          const v = realCellValue(group, row.master_id, d, label);
          const n = v === null ? NaN : parseFloat(v);
          if (!isNaN(n)) sum += n;
        }
        colSums.set(key, sum);
      }
      map.set(row.master_id, colSums);
    }
    return map;
  }, [
    filteredRows, orderedSelectedColumns, dateColumns,
    ranklyRows, revenueRows, occupancyRows, bookPerDayRows, mmtRows, rateParityRows,
    bookingOverviewRows, bookingVisitConversionRows, bookingRankDataRows,
    masterIdToStayflexiHotelId, masterIdToRateParityId, masterIdToBookingId,
  ]);

  const rowTotalsByMasterIdB = useMemo(() => {
    const map = new Map();
    for (const row of filteredRows) {
      const colSums = new Map();
      for (const { key, label, group } of orderedSelectedColumns) {
        if (isLatestOnlyColumn(group, label) || isRevenuePctTotalColumn(group, label)) {
          continue;
        }
        let sum = 0;
        for (const d of dateColumnsB) {
          const v = realCellValue(group, row.master_id, d, label);
          const n = v === null ? NaN : parseFloat(v);
          if (!isNaN(n)) sum += n;
        }
        colSums.set(key, sum);
      }
      map.set(row.master_id, colSums);
    }
    return map;
  }, [
    filteredRows, orderedSelectedColumns, dateColumnsB,
    ranklyRows, revenueRows, occupancyRows, bookPerDayRows, mmtRows, rateParityRows,
    bookingOverviewRows, bookingVisitConversionRows, bookingRankDataRows,
    masterIdToStayflexiHotelId, masterIdToRateParityId, masterIdToBookingId,
  ]);

  const rows = useMemo(() => {
    const list = [...filteredRows];
    list.sort((a, b) => {
      if (sortKey === "property") {
        const cmp = a.property.localeCompare(b.property);
        return sortDirection === "desc" ? -cmp : cmp;
      }
      if (sortKey.startsWith("TOTAL::")) {
        const key = sortKey.slice("TOTAL::".length);
        const sep = key.indexOf("::");
        const grp = key.slice(0, sep);
        const lbl = key.slice(sep + 2);
        if (isLatestOnlyColumn(grp, lbl)) {
          const va = latestValueForColumn(grp, a.master_id, lbl);
          const vb = latestValueForColumn(grp, b.master_id, lbl);
          const na = va === null ? null : parseFloat(va);
          const nb = vb === null ? null : parseFloat(vb);
          if (na === null && nb === null) return 0;
          if (na === null) return 1;
          if (nb === null) return -1;
          const cmp = na - nb;
          return sortDirection === "desc" ? -cmp : cmp;
        }
        if (isRevenuePctTotalColumn(grp, lbl)) {
          const targetA = revenueTargetSumAcrossDates(a.master_id);
          const targetB = revenueTargetSumAcrossDates(b.master_id);
          const na = targetA > 0 ? (revenueRevenueSumAcrossDates(a.master_id) / targetA) * 100 : null;
          const nb = targetB > 0 ? (revenueRevenueSumAcrossDates(b.master_id) / targetB) * 100 : null;
          if (na === null && nb === null) return 0;
          if (na === null) return 1;
          if (nb === null) return -1;
          const cmp = na - nb;
          return sortDirection === "desc" ? -cmp : cmp;
        }
        const mode = metricDisplayModes[key] || "sum";
        const divisor = dateColumns.length || 1;
        const sumA = rowTotalsByMasterId.get(a.master_id)?.get(key);
        const sumB = rowTotalsByMasterId.get(b.master_id)?.get(key);
        const na = sumA === undefined ? null : mode === "sum" ? sumA : sumA / divisor;
        const nb = sumB === undefined ? null : mode === "sum" ? sumB : sumB / divisor;
        if (na === null && nb === null) return 0;
        if (na === null) return 1;
        if (nb === null) return -1;
        const cmp = na - nb;
        return sortDirection === "desc" ? -cmp : cmp;
      }
      if (sortKey.startsWith("TOTAL_B::")) {
        const key = sortKey.slice("TOTAL_B::".length);
        const sep = key.indexOf("::");
        const grp = key.slice(0, sep);
        const lbl = key.slice(sep + 2);
        if (isLatestOnlyColumn(grp, lbl)) {
          const va = latestValueForColumn(grp, a.master_id, lbl, dateColumnsB);
          const vb = latestValueForColumn(grp, b.master_id, lbl, dateColumnsB);
          const na = va === null ? null : parseFloat(va);
          const nb = vb === null ? null : parseFloat(vb);
          if (na === null && nb === null) return 0;
          if (na === null) return 1;
          if (nb === null) return -1;
          const cmp = na - nb;
          return sortDirection === "desc" ? -cmp : cmp;
        }
        if (isRevenuePctTotalColumn(grp, lbl)) {
          const targetA = revenueTargetSumAcrossDates(a.master_id, dateColumnsB);
          const targetB = revenueTargetSumAcrossDates(b.master_id, dateColumnsB);
          const na = targetA > 0 ? (revenueRevenueSumAcrossDates(a.master_id, dateColumnsB) / targetA) * 100 : null;
          const nb = targetB > 0 ? (revenueRevenueSumAcrossDates(b.master_id, dateColumnsB) / targetB) * 100 : null;
          if (na === null && nb === null) return 0;
          if (na === null) return 1;
          if (nb === null) return -1;
          const cmp = na - nb;
          return sortDirection === "desc" ? -cmp : cmp;
        }
        const mode = metricDisplayModes[key] || "sum";
        const divisor = dateColumnsB.length || 1;
        const sumA = rowTotalsByMasterIdB.get(a.master_id)?.get(key);
        const sumB = rowTotalsByMasterIdB.get(b.master_id)?.get(key);
        const na = sumA === undefined ? null : mode === "sum" ? sumA : sumA / divisor;
        const nb = sumB === undefined ? null : mode === "sum" ? sumB : sumB / divisor;
        if (na === null && nb === null) return 0;
        if (na === null) return 1;
        if (nb === null) return -1;
        const cmp = na - nb;
        return sortDirection === "desc" ? -cmp : cmp;
      }
      const firstSep = sortKey.indexOf("::");
      const date = sortKey.slice(0, firstSep);
      const groupAndLabel = sortKey.slice(firstSep + 2);
      const secondSep = groupAndLabel.indexOf("::");
      const group = groupAndLabel.slice(0, secondSep);
      const label = groupAndLabel.slice(secondSep + 2);
      const va = realCellValue(group, a.master_id, date, label);
      const vb = realCellValue(group, b.master_id, date, label);
      const na = va === null ? null : parseFloat(va);
      const nb = vb === null ? null : parseFloat(vb);
      if (na === null && nb === null) return 0;
      if (na === null) return 1;
      if (nb === null) return -1;
      const cmp = na - nb;
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return list;
  }, [
    filteredRows, sortDirection, sortKey, rowTotalsByMasterId, rowTotalsByMasterIdB, metricDisplayModes, dateColumns, dateColumnsB,
    ranklyRows, revenueRows, occupancyRows, bookPerDayRows, mmtRows, rateParityRows,
    bookingOverviewRows, bookingVisitConversionRows, bookingRankDataRows,
    masterIdToStayflexiHotelId, masterIdToRateParityId, masterIdToBookingId,
  ]);

  function ranklyValue(masterId, date, platformLabel) {
    const avgRating = ranklyIndex.get(`${masterId}|${date}|${norm(platformLabel)}`);
    if (avgRating === null || avgRating === undefined) return null;
    return Number(avgRating).toFixed(2);
  }

  function revenueValue(masterId, date, metric) {
    const entry = revenueIndex.get(`${masterId}|${date}`);
    if (!entry) return null;
    if (metric === "TARGET") {
      return entry.target ? Number(entry.target).toFixed(0) : null;
    }
    if (metric === "REVENUE") {
      return entry.totalRevenue ? entry.totalRevenue.toFixed(0) : null;
    }
    if (metric === "% REV") {
      const target = Number(entry.target) || 0;
      if (!target) return null;
      return ((entry.totalRevenue / target) * 100).toFixed(1);
    }
    return null;
  }

  function occupancyValue(masterId, date, metric) {
    const row = occupancyIndex.overallByKey.get(`${masterId}|${date}`);
    if (!row) return null;
    if (metric === "TOTAL ROOMS") return row.overall_total_rooms ?? null;
    if (metric === "AVAILABLE") return row.overall_available_rooms ?? null;
    if (metric === "SOLD") return row.overall_used_rooms ?? null;
    if (metric === "OCCUPANCY %") {
      return row.overall_occupancy_pct !== null && row.overall_occupancy_pct !== undefined
        ? Number(row.overall_occupancy_pct).toFixed(1)
        : null;
    }
    if (metric === "ADR") {
      const allCategoryRows = occupancyIndex.rowsByMasterDate.get(`${masterId}|${date}`) || [];
      let totalRevenue = 0;
      let totalSold = 0;
      for (const r of allCategoryRows) {
        const sold = Number(r.sold_rooms) || 0;
        const rate = Number(r.rate) || 0;
        totalRevenue += sold * rate;
        totalSold += sold;
      }
      if (totalSold === 0) return null;
      return (totalRevenue / totalSold).toFixed(0);
    }
    return null;
  }

  function toggleExpanded(masterId) {
    setExpandedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(masterId)) next.delete(masterId);
      else next.add(masterId);
      return next;
    });
  }

  function getRoomCategoriesForProperty(masterId) {
    const set = occupancyIndex.categoriesByMaster.get(masterId);
    return set ? Array.from(set).sort() : [];
  }

  function occupancyValueForCategory(masterId, date, metric, categoryName) {
    const row = occupancyIndex.byMasterDateCategory.get(`${masterId}|${date}|${categoryName}`);
    if (!row) return null;
    if (metric === "TOTAL ROOMS") return row.total_rooms ?? null;
    if (metric === "AVAILABLE") return row.available_rooms ?? null;
    if (metric === "SOLD") return row.sold_rooms ?? null;
    if (metric === "OCCUPANCY %") {
      return row.occupancy_pct !== null && row.occupancy_pct !== undefined
        ? Number(row.occupancy_pct).toFixed(1)
        : null;
    }
    if (metric === "ADR") {
      return row.rate !== null && row.rate !== undefined ? Number(row.rate).toFixed(0) : null;
    }
    return null;
  }

  function bookPerDayValue(masterId, date, metric) {
    const hotelId = masterIdToStayflexiHotelId.get(masterId);
    if (!hotelId) return null;
    const entry = bookPerDayIndex.get(`${hotelId}|${date}`);
    if (!entry) return null;
    if (metric === "BOOKINGS") return entry.bookings || null;
    if (metric === "RN") return entry.roomNights || null;
    if (metric === "REVENUE") return entry.revenue ? entry.revenue.toFixed(0) : null;
    if (metric === "ADR") return entry.roomNights > 0 ? (entry.revenue / entry.roomNights).toFixed(0) : null;
    return null;
  }

  function mmtValue(masterId, date, metric) {
    const realMetricName = MMT_METRIC_ALIASES[metric] || metric;
    const value = mmtIndex.get(`${masterId}|${date}|${norm(realMetricName)}`);
    if (value === null || value === undefined) return null;
    return Number(value).toFixed(2);
  }

  function rateParityValue(masterId, date, otaName) {
    const rateParityId = masterIdToRateParityId.get(masterId);
    if (!rateParityId) return null;
    const price = rateParityIndex.get(`${rateParityId}|${date}|${norm(otaName)}`);
    if (price === null || price === undefined) return null;
    return Number(price).toFixed(0);
  }

  function bookingOverviewValue(masterId, date, metric) {
    const bookingId = masterIdToBookingId.get(masterId);
    if (!bookingId) return null;
    const row = bookingOverviewIndex.get(`${bookingId}|${date}`);
    if (!row) return null;
    if (metric === "RN (REVX)") return row.room_nights_revx !== null && row.room_nights_revx !== undefined ? Number(row.room_nights_revx).toFixed(1) : null;
    if (metric === "RN (MKT)") return row.room_nights_market !== null && row.room_nights_market !== undefined ? Number(row.room_nights_market).toFixed(1) : null;
    if (metric === "ADR (REVX)") return row.adr_revx !== null && row.adr_revx !== undefined ? Number(row.adr_revx).toFixed(0) : null;
    if (metric === "ADR (MKT)") return row.adr_market !== null && row.adr_market !== undefined ? Number(row.adr_market).toFixed(0) : null;
    if (metric === "REVENUE (REVX)") return row.revenue_revx !== null && row.revenue_revx !== undefined ? Number(row.revenue_revx).toFixed(0) : null;
    if (metric === "REVENUE (MKT)") return row.revenue_market !== null && row.revenue_market !== undefined ? Number(row.revenue_market).toFixed(0) : null;
    return null;
  }

  function bookingVisitConversionValue(masterId, date, metric) {
    const bookingId = masterIdToBookingId.get(masterId);
    if (!bookingId) return null;
    const row = bookingVisitConversionIndex.get(`${bookingId}|${date}`);
    if (!row) return null;
    if (metric === "BOOKINGS") return row.daily_bookings !== null && row.daily_bookings !== undefined ? Number(row.daily_bookings) : null;
    if (metric === "CONV%") return row.conversion_pct !== null && row.conversion_pct !== undefined ? Number(row.conversion_pct).toFixed(1) : null;
    if (metric === "VISITS") return row.page_views !== null && row.page_views !== undefined ? Number(row.page_views) : null;
    return null;
  }

  function bookingRankValue(masterId, _date, metric) {
    if (metric !== "RANK") return null;
    const bookingId = masterIdToBookingId.get(masterId);
    if (!bookingId) return null;
    const row = bookingRankIndex.get(bookingId);
    if (!row || row.rank_in_city === null || row.rank_in_city === undefined) return null;
    const total = row.total_rank_in_city !== null && row.total_rank_in_city !== undefined ? row.total_rank_in_city : "?";
    return `${row.rank_in_city}/${total}`;
  }

  function bookingDashboardValue(masterId, date, label) {
    if (label === "RANK") return bookingRankValue(masterId, date, label);
    if (label === "BOOKINGS" || label === "CONV%" || label === "VISITS") {
      return bookingVisitConversionValue(masterId, date, label);
    }
    return bookingOverviewValue(masterId, date, label);
  }

  function realCellValue(group, masterId, date, label) {
    if (!masterId) return null;
    if (group === "RANKLY") return ranklyValue(masterId, date, label);
    if (group === "REVENUE DASHBOARD") return revenueValue(masterId, date, label);
    if (group === "OCCUPANCY DASHBOARD") return occupancyValue(masterId, date, label);
    if (group === "BOOK PER DAY") return bookPerDayValue(masterId, date, label);
    if (group === "MMT DASHBOARD") {
      if (label === "RATING (MMT)") return ranklyValue(masterId, date, "MAKEMYTRIP");
      if (label === "RATING (GOIBIBO)") return ranklyValue(masterId, date, "GOIBIBO");
      return mmtValue(masterId, date, label);
    }
    if (group === "BOOKING DASHBOARD") return bookingDashboardValue(masterId, date, label);
    if (group === "RATE PARITY") return rateParityValue(masterId, date, label);
    return null;
  }

  function isLatestOnlyColumn(group, label) {
    if (group === "RANKLY") return true;
    if (group === "MMT DASHBOARD" && (label === "RATING (MMT)" || label === "RATING (GOIBIBO)")) return true;
    return false;
  }
  function latestValueForColumn(group, masterId, label, dates = dateColumns) {
    for (let i = dates.length - 1; i >= 0; i--) {
      const v = realCellValue(group, masterId, dates[i], label);
      if (v !== null) return v;
    }
    return null;
  }

  function isRevenuePctTotalColumn(group, label) {
    return group === "REVENUE DASHBOARD" && label === "% REV";
  }
  function revenueTargetSumAcrossDates(masterId, dates = dateColumns) {
    let sum = 0;
    for (const d of dates) {
      const t = revenueValue(masterId, d, "TARGET");
      if (t !== null) sum += parseFloat(t) || 0;
    }
    return sum;
  }
  function revenueRevenueSumAcrossDates(masterId, dates = dateColumns) {
    let sum = 0;
    for (const d of dates) {
      const r = revenueValue(masterId, d, "REVENUE");
      if (r !== null) sum += parseFloat(r) || 0;
    }
    return sum;
  }

  function computeDisplayedForPeriod(masterId, group, label, key, dates, rowTotalsMap) {
    if (group === "BOOKING DASHBOARD" && label === "RANK") return null;
    if (isLatestOnlyColumn(group, label)) {
      const latest = latestValueForColumn(group, masterId, label, dates);
      return latest === null ? null : Number(latest);
    }
    if (isRevenuePctTotalColumn(group, label)) {
      const targetSum = revenueTargetSumAcrossDates(masterId, dates);
      if (targetSum <= 0) return null;
      const revSum = revenueRevenueSumAcrossDates(masterId, dates);
      return (revSum / targetSum) * 100;
    }
    const sum = rowTotalsMap.get(masterId)?.get(key) ?? 0;
    const mode = metricDisplayModes[key] || "sum";
    const divisor = dates.length || 1;
    return mode === "sum" ? sum : sum / divisor;
  }

  function computeVariancePct(masterId, group, label, key) {
    const a = computeDisplayedForPeriod(masterId, group, label, key, dateColumns, rowTotalsByMasterId);
    const b = computeDisplayedForPeriod(masterId, group, label, key, dateColumnsB, rowTotalsByMasterIdB);
    if (a === null || b === null || a === 0) return null;
    return ((b - a) / a) * 100;
  }

  function computeGrandTotalForPeriod(group, label, key, dates, rowTotalsMap) {
    if (isRevenuePctTotalColumn(group, label)) {
      const portfolioTarget = rows.reduce((acc, row) => acc + revenueTargetSumAcrossDates(row.master_id, dates), 0);
      const portfolioRevenue = rows.reduce((acc, row) => acc + revenueRevenueSumAcrossDates(row.master_id, dates), 0);
      return portfolioTarget > 0 ? (portfolioRevenue / portfolioTarget) * 100 : null;
    }
    const grandSum = rows.reduce((acc, row) => acc + (rowTotalsMap.get(row.master_id)?.get(key) ?? 0), 0);
    const mode = metricDisplayModes[key] || "sum";
    return mode === "sum" ? grandSum : grandSum / (rows.length || 1);
  }

  if (loading && realProperties.length === 0) {
    return <div className="rd-blank-tab">Loading data from Rankly, Revenue, Occupancy, Book Per Day, MMT, Rate Parity, and Booking...</div>;
  }

  return (
    <>
      {loadErrors.length > 0 && (
        <div style={{ padding: "8px 16px", color: "#b91c1c", fontSize: 13 }}>
          Couldn't load: {loadErrors.join(", ")} — their columns will show ND until this is retried.
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar mv-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
        <div className="rd-filter-row">
          <MultiSelectFilter
            label="Property Name"
            options={propertyOptions}
            selected={selectedProperties}
            onChange={setSelectedProperties}
            onClear={() => setSelectedProperties([])}
          />
          <MultiSelectFilter
            label="Location"
            options={locationOptions}
            selected={selectedLocations}
            onChange={setSelectedLocations}
            onClear={() => setSelectedLocations([])}
          />
          <MultiSelectFilter
            label="Agent"
            options={agentOptions}
            selected={selectedAgents}
            onChange={setSelectedAgents}
            onClear={() => setSelectedAgents([])}
          />
          <ColumnsFilter
            selected={selectedColumns}
            onChange={setSelectedColumns}
            onClear={() => setSelectedColumns([])}
          />
          <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
          <MonthFilter
            availableMonths={availableMonths}
            selectedMonth={selectedMonth}
            onChange={handleMonthChange}
            onClear={clearMonth}
          />
          <div className="rd-filter-group mv-pct-group">
            <FilterLabel label="% Rev ≤" onClear={() => setRevPctFilterMax(null)} />
            <div style={{ display: "flex", gap: 4 }}>
              {[30, 50, 70, 90].map((threshold) => (
                <button
                  key={threshold}
                  onClick={() => setRevPctFilterMax((prev) => (prev === threshold ? null : threshold))}
                  className="rd-dropdown-btn"
                  style={
                    revPctFilterMax === threshold
                      ? { backgroundColor: "#1e293b", color: "#fff", borderColor: "#1e293b" }
                      : undefined
                  }
                >
                  ≤{threshold}%
                </button>
              ))}
            </div>
          </div>
          <div className="occ-date-and-clear">
            <DateRangeControl
              availableDates={availableDates}
              fromDate={fromDate}
              toDate={toDate}
              onRangeChange={applyPeriodARange}
              onClear={clearDates}
              onToday={goToToday}
              label="PERIOD A"
            />
            <DateRangeControl
              availableDates={availableDates}
              fromDate={fromDateB}
              toDate={toDateB}
              onRangeChange={applyPeriodBRange}
              onClear={clearDatesB}
              onToday={goToTodayB}
              label="PERIOD B"
            />
            <div className="rd-actions">
              <button onClick={clearAllFilters} className="rd-btn-clear">
                <X size={14} />
                Clear all filters
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: 260 }} />
                {orderedSelectedColumns.length > 0 &&
                  orderedSelectedColumns.map(({ key }) => <col key={`total-${key}-col`} style={{ width: 100 }} />)}
                {orderedSelectedColumns.length > 0 &&
                  orderedSelectedColumns.map(({ key }) => <col key={`totalB-${key}-col`} style={{ width: 100 }} />)}
                {orderedSelectedColumns.length > 0 &&
                  orderedSelectedColumns.map(({ key }) => <col key={`variance-${key}-col`} style={{ width: 100 }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th
                    rowSpan={orderedSelectedColumns.length > 0 ? 2 : 1}
                    onClick={() => handleHeaderClick("property")}
                    style={{ left: 0, width: 260 }}
                    className="rd-th-fixed"
                  >
                    <span className="rd-count-badge">{rows.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "\u25b2" : "\u25bc"}</span>
                    )}
                  </th>
                  {orderedSelectedColumns.length > 0 && (
                    <th colSpan={orderedSelectedColumns.length} className="occ-th-group rd-total-edge rd-total-header-blank">
                      Period A Total
                    </th>
                  )}
                  {orderedSelectedColumns.length > 0 && (
                    <th colSpan={orderedSelectedColumns.length} className="occ-th-group rd-total-edge rd-total-header-blank">
                      Period B Total
                    </th>
                  )}
                  {orderedSelectedColumns.length > 0 && (
                    <th colSpan={orderedSelectedColumns.length} className="occ-th-group rd-total-edge rd-total-header-blank">
                      % Variance
                    </th>
                  )}
                </tr>
                {orderedSelectedColumns.length > 0 && (
                  <tr>
                    {orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                      const totalSortKey = `TOTAL::${key}`;
                      const isLatest = isLatestOnlyColumn(group, label);
                      const isRevPct = isRevenuePctTotalColumn(group, label);
                      const hideToggle = isLatest || isRevPct;
                      return (
                        <th key={`total-${key}`} className={`occ-th-metric rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                          <div
                            style={{ marginBottom: 2, cursor: "pointer" }}
                            onClick={() => handleHeaderClick(totalSortKey)}
                          >
                            {label} {isLatest ? "LATEST" : "TOTAL"}
                            {sortKey === totalSortKey && (
                              <span className="rd-sort-indicator">{sortDirection === "asc" ? "\u25b2" : "\u25bc"}</span>
                            )}
                          </div>
                          {!hideToggle && (
                            <select
                              value={metricDisplayModes[key] || "sum"}
                              onChange={(e) => setMetricDisplayModes((prev) => ({ ...prev, [key]: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              className="rd-total-select"
                            >
                              <option value="sum">Sum</option>
                              <option value="average">Average</option>
                            </select>
                          )}
                        </th>
                      );
                    })}
                    {orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                      const totalSortKeyB = `TOTAL_B::${key}`;
                      const isLatest = isLatestOnlyColumn(group, label);
                      const isRevPct = isRevenuePctTotalColumn(group, label);
                      const hideToggle = isLatest || isRevPct;
                      return (
                        <th key={`totalB-${key}`} className={`occ-th-metric rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                          <div
                            style={{ marginBottom: 2, cursor: "pointer" }}
                            onClick={() => handleHeaderClick(totalSortKeyB)}
                          >
                            {label} {isLatest ? "LATEST" : "TOTAL"}
                            {sortKey === totalSortKeyB && (
                              <span className="rd-sort-indicator">{sortDirection === "asc" ? "\u25b2" : "\u25bc"}</span>
                            )}
                          </div>
                          {!hideToggle && (
                            <select
                              value={metricDisplayModes[key] || "sum"}
                              onChange={(e) => setMetricDisplayModes((prev) => ({ ...prev, [key]: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              className="rd-total-select"
                            >
                              <option value="sum">Sum</option>
                              <option value="average">Average</option>
                            </select>
                          )}
                        </th>
                      );
                    })}
                    {orderedSelectedColumns.map(({ key, label }, mIdx) => (
                      <th key={`variance-${key}`} className={`occ-th-metric rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                        {label}
                      </th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  const categories = getRoomCategoriesForProperty(row.master_id);
                  const isExpanded = expandedProperties.has(row.master_id);
                  return (
                    <React.Fragment key={row.property}>
                      <tr className={`rd-matrix-row ${rowBgClass}`}>
                        <td style={{ left: 0, width: 260 }} className={`rd-td-fixed ${rowBgClass}`}>
                          {categories.length > 0 && (
                            <button
                              onClick={() => toggleExpanded(row.master_id)}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginRight: 4, verticalAlign: "-2px" }}
                              title={isExpanded ? "Collapse room categories" : "Expand room categories"}
                            >
                              <ChevronDown
                                size={14}
                                style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}
                              />
                            </button>
                          )}
                          <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(row.property) }} />
                          {row.property}
                        </td>
                        {orderedSelectedColumns.length > 0 &&
                          orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                            let displayed;
                            let isNd = false;
                            if (isLatestOnlyColumn(group, label)) {
                              const latest = latestValueForColumn(group, row.master_id, label);
                              if (latest === null) {
                                isNd = true;
                                displayed = null;
                              } else {
                                displayed = Number(latest);
                              }
                            } else if (isRevenuePctTotalColumn(group, label)) {
                              const targetSum = revenueTargetSumAcrossDates(row.master_id);
                              const revSum = revenueRevenueSumAcrossDates(row.master_id);
                              if (targetSum > 0) {
                                displayed = (revSum / targetSum) * 100;
                              } else {
                                isNd = true;
                                displayed = null;
                              }
                            } else {
                              const sum = rowTotalsByMasterId.get(row.master_id)?.get(key) ?? 0;
                              const mode = metricDisplayModes[key] || "sum";
                              const divisor = dateColumns.length || 1;
                              displayed = mode === "sum" ? sum : sum / divisor;
                            }
                            // Same color-coding as the real Revenue Dashboard tab —
                            // the REVENUE cell's background reflects that period's
                            // % of target (getRevenueColor), not a plain row tint.
                            const isRevenueCell = group === "REVENUE DASHBOARD" && label === "REVENUE";
                            let revenueBg;
                            if (isRevenueCell && !isNd) {
                              const targetSum = revenueTargetSumAcrossDates(row.master_id);
                              const pct = targetSum > 0 ? (revenueRevenueSumAcrossDates(row.master_id) / targetSum) * 100 : null;
                              revenueBg = getRevenueColor(pct);
                            }
                            return (
                              <td
                                key={`total-${key}`}
                                className={`occ-td-t${isRevenueCell ? "" : ` rd-total-cell-plain ${rowBgClass}`}${mIdx === 0 ? " rd-total-edge" : ""}`}
                                style={isRevenueCell ? { fontWeight: 700, backgroundColor: revenueBg } : { fontWeight: 700 }}
                              >
                                {isNd ? (
                                  <span style={{ color: "#9ca3af", fontStyle: "italic" }}>ND</span>
                                ) : group === "BOOKING DASHBOARD" && label === "RANK" ? (
                                  displayed.toFixed(2)
                                ) : (
                                  formatCellValue(group, label, displayed)
                                )}
                              </td>
                            );
                          })}
                        {orderedSelectedColumns.length > 0 &&
                          orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                            let displayed;
                            let isNd = false;
                            if (isLatestOnlyColumn(group, label)) {
                              const latest = latestValueForColumn(group, row.master_id, label, dateColumnsB);
                              if (latest === null) {
                                isNd = true;
                                displayed = null;
                              } else {
                                displayed = Number(latest);
                              }
                            } else if (isRevenuePctTotalColumn(group, label)) {
                              const targetSum = revenueTargetSumAcrossDates(row.master_id, dateColumnsB);
                              const revSum = revenueRevenueSumAcrossDates(row.master_id, dateColumnsB);
                              if (targetSum > 0) {
                                displayed = (revSum / targetSum) * 100;
                              } else {
                                isNd = true;
                                displayed = null;
                              }
                            } else {
                              const sum = rowTotalsByMasterIdB.get(row.master_id)?.get(key) ?? 0;
                              const mode = metricDisplayModes[key] || "sum";
                              const divisor = dateColumnsB.length || 1;
                              displayed = mode === "sum" ? sum : sum / divisor;
                            }
                            const isRevenueCell = group === "REVENUE DASHBOARD" && label === "REVENUE";
                            let revenueBg;
                            if (isRevenueCell && !isNd) {
                              const targetSum = revenueTargetSumAcrossDates(row.master_id, dateColumnsB);
                              const pct = targetSum > 0 ? (revenueRevenueSumAcrossDates(row.master_id, dateColumnsB) / targetSum) * 100 : null;
                              revenueBg = getRevenueColor(pct);
                            }
                            return (
                              <td
                                key={`totalB-${key}`}
                                className={`occ-td-t${isRevenueCell ? "" : ` rd-total-cell-plain ${rowBgClass}`}${mIdx === 0 ? " rd-total-edge" : ""}`}
                                style={isRevenueCell ? { fontWeight: 700, backgroundColor: revenueBg } : { fontWeight: 700 }}
                              >
                                {isNd ? (
                                  <span style={{ color: "#9ca3af", fontStyle: "italic" }}>ND</span>
                                ) : group === "BOOKING DASHBOARD" && label === "RANK" ? (
                                  displayed.toFixed(2)
                                ) : (
                                  formatCellValue(group, label, displayed)
                                )}
                              </td>
                            );
                          })}
                        {orderedSelectedColumns.length > 0 &&
                          orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                            const variancePct = computeVariancePct(row.master_id, group, label, key);
                            return (
                              <td key={`variance-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ fontWeight: 700 }}>
                                {variancePct === null ? (
                                  <span style={{ color: "#94a3b8" }}>—</span>
                                ) : (
                                  <span style={{ color: variancePct >= 0 ? "#166534" : "#7f1d1d" }}>
                                    {variancePct.toFixed(3)}%
                                  </span>
                                )}
                              </td>
                            );
                          })}
                      </tr>
                      {isExpanded &&
                        categories.map((categoryName) => (
                          <tr key={`${row.property}-${categoryName}`} className={`rd-matrix-row ${rowBgClass}`} style={{ fontStyle: "italic" }}>
                            <td style={{ left: 0, width: 260, paddingLeft: 32 }} className={`rd-td-fixed ${rowBgClass}`}>
                              {categoryName}
                            </td>
                            {orderedSelectedColumns.length > 0 &&
                              orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                                if (group !== "OCCUPANCY DASHBOARD") {
                                  return (
                                    <td key={`total-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#d1d5db" }}>
                                      —
                                    </td>
                                  );
                                }
                                const sum = dateColumns.reduce((acc, d) => {
                                  const v = occupancyValueForCategory(row.master_id, d, label, categoryName);
                                  const n = v === null ? NaN : parseFloat(v);
                                  return acc + (isNaN(n) ? 0 : n);
                                }, 0);
                                const mode = metricDisplayModes[key] || "sum";
                                const divisor = dateColumns.length || 1;
                                const displayed = mode === "sum" ? sum : sum / divisor;
                                return (
                                  <td key={`total-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                                    {formatCellValue("OCCUPANCY DASHBOARD", label, displayed)}
                                  </td>
                                );
                              })}
                            {orderedSelectedColumns.length > 0 &&
                              orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                                if (group !== "OCCUPANCY DASHBOARD") {
                                  return (
                                    <td key={`totalB-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#d1d5db" }}>
                                      —
                                    </td>
                                  );
                                }
                                const sum = dateColumnsB.reduce((acc, d) => {
                                  const v = occupancyValueForCategory(row.master_id, d, label, categoryName);
                                  const n = v === null ? NaN : parseFloat(v);
                                  return acc + (isNaN(n) ? 0 : n);
                                }, 0);
                                const mode = metricDisplayModes[key] || "sum";
                                const divisor = dateColumnsB.length || 1;
                                const displayed = mode === "sum" ? sum : sum / divisor;
                                return (
                                  <td key={`totalB-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                                    {formatCellValue("OCCUPANCY DASHBOARD", label, displayed)}
                                  </td>
                                );
                              })}
                            {orderedSelectedColumns.length > 0 &&
                              orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                                if (group !== "OCCUPANCY DASHBOARD") {
                                  return (
                                    <td key={`variance-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#d1d5db" }}>
                                      —
                                    </td>
                                  );
                                }
                                const sumA = dateColumns.reduce((acc, d) => {
                                  const v = occupancyValueForCategory(row.master_id, d, label, categoryName);
                                  const n = v === null ? NaN : parseFloat(v);
                                  return acc + (isNaN(n) ? 0 : n);
                                }, 0);
                                const sumB = dateColumnsB.reduce((acc, d) => {
                                  const v = occupancyValueForCategory(row.master_id, d, label, categoryName);
                                  const n = v === null ? NaN : parseFloat(v);
                                  return acc + (isNaN(n) ? 0 : n);
                                }, 0);
                                const mode = metricDisplayModes[key] || "sum";
                                const a = mode === "sum" ? sumA : sumA / (dateColumns.length || 1);
                                const b = mode === "sum" ? sumB : sumB / (dateColumnsB.length || 1);
                                const variancePct = a === 0 ? null : ((b - a) / a) * 100;
                                return (
                                  <td key={`variance-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                                    {variancePct === null ? (
                                      <span style={{ color: "#94a3b8" }}>—</span>
                                    ) : (
                                      <span style={{ color: variancePct >= 0 ? "#166534" : "#7f1d1d" }}>
                                        {variancePct.toFixed(3)}%
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {orderedSelectedColumns.length > 0 && (
                <tfoot>
                  <tr className="booking-total-row">
                    <td style={{ left: 0, width: 260 }} className="rd-td-fixed">
                      TOTAL
                    </td>
                    {orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                      if (isLatestOnlyColumn(group, label)) {
                        return (
                          <td key={`foot-total-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#94a3b8" }}>
                            —
                          </td>
                        );
                      }
                      if (isRevenuePctTotalColumn(group, label)) {
                        const portfolioTarget = rows.reduce((acc, row) => acc + revenueTargetSumAcrossDates(row.master_id), 0);
                        const portfolioRevenue = rows.reduce((acc, row) => acc + revenueRevenueSumAcrossDates(row.master_id), 0);
                        const pct = portfolioTarget > 0 ? (portfolioRevenue / portfolioTarget) * 100 : null;
                        return (
                          <td key={`foot-total-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                            {pct === null ? (
                              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>ND</span>
                            ) : (
                              formatCellValue(group, label, pct)
                            )}
                          </td>
                        );
                      }
                      const grandSum = rows.reduce((acc, row) => acc + (rowTotalsByMasterId.get(row.master_id)?.get(key) ?? 0), 0);
                      const mode = PERCENT_LABELS.has(label) ? "average" : (metricDisplayModes[key] || "sum");
                      const displayed = mode === "sum" ? grandSum : grandSum / (rows.length || 1);
                      return (
                        <td key={`foot-total-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                          {group === "BOOKING DASHBOARD" && label === "RANK" ? displayed.toFixed(2) : formatCellValue(group, label, displayed)}
                        </td>
                      );
                    })}
                    {orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                      if (isLatestOnlyColumn(group, label)) {
                        return (
                          <td key={`foot-totalB-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#94a3b8" }}>
                            —
                          </td>
                        );
                      }
                      if (isRevenuePctTotalColumn(group, label)) {
                        const portfolioTarget = rows.reduce((acc, row) => acc + revenueTargetSumAcrossDates(row.master_id, dateColumnsB), 0);
                        const portfolioRevenue = rows.reduce((acc, row) => acc + revenueRevenueSumAcrossDates(row.master_id, dateColumnsB), 0);
                        const pct = portfolioTarget > 0 ? (portfolioRevenue / portfolioTarget) * 100 : null;
                        return (
                          <td key={`foot-totalB-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                            {pct === null ? (
                              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>ND</span>
                            ) : (
                              formatCellValue(group, label, pct)
                            )}
                          </td>
                        );
                      }
                      const grandSum = rows.reduce((acc, row) => acc + (rowTotalsByMasterIdB.get(row.master_id)?.get(key) ?? 0), 0);
                      const mode = PERCENT_LABELS.has(label) ? "average" : (metricDisplayModes[key] || "sum");
                      const displayed = mode === "sum" ? grandSum : grandSum / (rows.length || 1);
                      return (
                        <td key={`foot-totalB-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                          {group === "BOOKING DASHBOARD" && label === "RANK" ? displayed.toFixed(2) : formatCellValue(group, label, displayed)}
                        </td>
                      );
                    })}
                    {orderedSelectedColumns.map(({ key, label, group }, mIdx) => {
                      if (isLatestOnlyColumn(group, label) || (group === "BOOKING DASHBOARD" && label === "RANK")) {
                        return (
                          <td key={`foot-variance-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`} style={{ color: "#94a3b8" }}>
                            —
                          </td>
                        );
                      }
                      const a = computeGrandTotalForPeriod(group, label, key, dateColumns, rowTotalsByMasterId);
                      const b = computeGrandTotalForPeriod(group, label, key, dateColumnsB, rowTotalsByMasterIdB);
                      const variancePct = (a === null || b === null || a === 0) ? null : ((b - a) / a) * 100;
                      return (
                        <td key={`foot-variance-${key}`} className={`occ-td-t${mIdx === 0 ? " rd-total-edge" : ""}`}>
                          {variancePct === null ? (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          ) : (
                            <span style={{ color: variancePct >= 0 ? "#166534" : "#7f1d1d" }}>
                              {variancePct.toFixed(3)}%
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
